import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../NavBar.js';
import styles from '../styles/ActiveAccounts.module.css';

// Firebase
import { auth, db } from '../../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy 
} from 'firebase/firestore';

export default function ActiveAccounts() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);
  const [cuentasActivas, setCuentasActivas] = useState([]);
  const [cuentasFiltradas, setCuentasFiltradas] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);

  // Estados para filtros
  const [plataformas, setPlataformas] = useState([]);
  const [serviceFilter, setServiceFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');

  // --- NUEVO: estado para el modal de renovación ---
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    cuenta: null,
    precio: null,
    moneda: 'COP',
    duracion: null,
    origen: null, // 'catalogo' o 'catalogoVendedores'
    loading: false,
    error: null
  });

  // Función para formatear moneda COP
  const formatCOP = (valor) => {
    if (valor == null || isNaN(valor)) return '—';
    try {
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(valor));
    } catch {
      return `${valor} COP`;
    }
  };

  const buscarPrecioServicio = async (cuenta) => {
    try {
      const nombreServicio = cuenta.plataforma?.trim() || cuenta.codigo?.trim();

      // ✅ 1. Buscar el rol del usuario
      const user = auth.currentUser;
      if (!user) throw new Error("Usuario no autenticado.");

      const userRef = collection(db, "Usuarios");
      const userQuery = query(userRef, where("uid", "==", user.uid));
      const userSnap = await getDocs(userQuery);

      let rolUsuario = "usuario";
      if (!userSnap.empty) {
        const userData = userSnap.docs[0].data();
        rolUsuario = userData.rol || "usuario";
      }

      // ✅ 2. Colección según el rol
      const coleccion = rolUsuario === "usuario" ? "catalogo" : "catalogoVendedores";

      // ✅ 3. Buscar servicio
      const colRef = collection(db, coleccion);
      const q = query(colRef, where("plataforma", "==", nombreServicio));
      const snap = await getDocs(q);

      if (!snap.empty) {
        const data = snap.docs[0].data();
        const precio =
          data.precioConDescuentoCOP ??
          data.precioBaseCOP ??
          null;
        const duracion = data.duracion || "N/A";
        const moneda = "COP";
        return { encontrado: true, precio, moneda, origen: coleccion, duracion };
      }

      return { encontrado: false };
    } catch (error) {
      console.error("❌ Error buscando precio del servicio:", error);
      return { encontrado: false };
    }
  };

  // --- NUEVO: abrir modal de renovación ---
  const abrirModalRenovacion = async (cuenta) => {
    setModalData((prev) => ({ ...prev, loading: true, error: null, cuenta, precio: null, origen: null }));
    setModalOpen(true);

    try {
      const res = await buscarPrecioServicio(cuenta);
      if (!res.encontrado) {
        setModalData((prev) => ({
          ...prev,
          loading: false,
          error: 'No encontramos el valor de renovación para este servicio en el catálogo.',
        }));
        return;
      }
      setModalData((prev) => ({
        ...prev,
        loading: false,
        precio: res.precio ?? null,
        moneda: res.moneda || 'COP',
        origen: res.origen,
        duracion: res.duracion || "N/A"
      }));
    } catch (e) {
      setModalData((prev) => ({
        ...prev,
        loading: false,
        error: 'Ocurrió un error buscando el precio. Intenta nuevamente.',
      }));
    }
  };

  // --- NUEVO: continuar al flujo de pago /renewal ---
  const continuarPagoRenovacion = () => {
    if (!modalData.cuenta) {
      setModalOpen(false);
      return;
    }
    const { cuenta, precio, moneda, origen } = modalData;
    navigate('/renewal', {
      state: {
        cuentaId: cuenta.id,
        codigo: cuenta.codigo,
        plataforma: cuenta.plataforma,
        tipoCuenta: cuenta.tipoCuenta,
        precio,
        moneda,
        origenCatalogo: origen,
      }
    });
  };

  // Función para formatear fecha
  const formatearFecha = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('es-CO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString('es-CO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      if (typeof timestamp === 'string') {
        return timestamp;
      }
      return 'Fecha no válida';
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Error en fecha';
    }
  };

  // Función para calcular días restantes
  const calcularDiasRestantes = (fechaCorte) => {
    try {
      if (!fechaCorte) return 0;
      let fechaCorteDate;
      if (fechaCorte.toDate) {
        fechaCorteDate = fechaCorte.toDate();
      } else if (fechaCorte instanceof Date) {
        fechaCorteDate = fechaCorte;
      } else if (typeof fechaCorte === 'string') {
        const partes = fechaCorte.split('/');
        if (partes.length === 3) {
          fechaCorteDate = new Date(partes[2], partes[1] - 1, partes[0]);
        }
      }
      if (!fechaCorteDate) return 0;
      const hoy = new Date();
      const diferenciaMilisegundos = fechaCorteDate.getTime() - hoy.getTime();
      const diasRestantes = Math.ceil(diferenciaMilisegundos / (1000 * 60 * 60 * 24));
      return diasRestantes > 0 ? diasRestantes : 0;
    } catch (error) {
      console.error('Error calculando días restantes:', error);
      return 0;
    }
  };

  // Función para obtener el estado de la cuenta
  const obtenerEstadoCuenta = (diasRestantes) => {
    if (diasRestantes === 0) return { texto: 'Expirada', color: '#ef4444' };
    if (diasRestantes <= 3) return { texto: 'Por vencer', color: '#f59e0b' };
    if (diasRestantes <= 7) return { texto: 'Próxima a vencer', color: '#eab308' };
    return { texto: 'Activa', color: '#22c55e' };
  };

  // NUEVA FUNCIÓN: Obtener credenciales de la colección Cuentas
  const obtenerCredencialesCuenta = async (codigo) => {
    try {
      const cuentasQuery = query(
        collection(db, 'Cuentas'),
        where('codigo', '==', codigo)
      );
      const cuentasSnapshot = await getDocs(cuentasQuery);
      if (!cuentasSnapshot.empty) {
        const cuentaDoc = cuentasSnapshot.docs[0];
        const cuentaData = cuentaDoc.data();
        return {
          emailCuenta: cuentaData.correo || 'No disponible',
          passwordCuenta: cuentaData.contrasena || 'No disponible'
        };
      } else {
        return {
          emailCuenta: 'No encontrado',
          passwordCuenta: 'No encontrado'
        };
      }
    } catch (error) {
      console.error('❌ Error obteniendo credenciales para', codigo, ':', error);
      return {
        emailCuenta: 'Error',
        passwordCuenta: 'Error'
      };
    }
  };

  // NUEVA FUNCIÓN: Cargar plataformas desde Firebase
  const cargarPlataformas = async () => {
    try {
      const plataformasSnapshot = await getDocs(collection(db, 'plataformas'));
      const plataformasData = plataformasSnapshot.docs.map(doc => doc.data().nombre);
      setPlataformas(plataformasData);
    } catch (error) {
      console.error('❌ Error cargando plataformas:', error);
    }
  };

  // Función para cargar cuentas activas desde ventasOnline
  const cargarCuentasActivas = async (userUid) => {
    try {
      setLoadingCuentas(true);
      let ventasQuery;
      let ventasSnapshot;
      try {
        ventasQuery = query(
          collection(db, 'ventasOnline'),
          where('uid', '==', userUid),
          where('activo', '==', true),
          orderBy('createdAt', 'desc')
        );
        ventasSnapshot = await getDocs(ventasQuery);
      } catch (indexError) {
        try {
          ventasQuery = query(
            collection(db, 'ventasOnline'),
            where('uid', '==', userUid),
            where('activo', '==', true)
          );
          ventasSnapshot = await getDocs(ventasQuery);
        } catch (activoError) {
          ventasQuery = query(
            collection(db, 'ventasOnline'),
            where('uid', '==', userUid)
          );
          ventasSnapshot = await getDocs(ventasQuery);
        }
      }
      
      const cuentas = [];
      for (const doc of ventasSnapshot.docs) {
        const data = doc.data();
        if (data.activo !== false) {
          const diasRestantes = calcularDiasRestantes(data.fechaCorte);
          const estado = obtenerEstadoCuenta(diasRestantes);
          const credenciales = await obtenerCredencialesCuenta(data.codigo);
          const nombrePerfil = data.nombrePerfil || 'No especificado';
          const pinPerfil = data.pinPerfil || null;
          cuentas.push({
            id: doc.id,
            codigo: data.codigo,
            plataforma: data.plataforma,
            fechaInicio: formatearFecha(data.fechaInicio),
            fechaCorte: formatearFecha(data.fechaCorte),
            diasRestantes: diasRestantes,
            estado: estado,
            correo: data.correo,
            numero: data.numero,
            medioPago: data.medioPago,
            idTransaccion: data.idTransaccion,
            tipoCuenta: data.tipoCuenta,
            diasDuracion: data.diasDuracion,
            nombreComprador: data.nombreComprador,
            createdAt: data.createdAt,
            nombrePerfil: nombrePerfil,
            pinPerfil: pinPerfil,
            numeroPerfil: data.numeroPerfil || null,
            emailCuenta: credenciales.emailCuenta,
            passwordCuenta: credenciales.passwordCuenta
          });
        }
      }
      cuentas.sort((a, b) => a.diasRestantes - b.diasRestantes);
      setCuentasActivas(cuentas);
      setCuentasFiltradas(cuentas);
    } catch (error) {
      console.error('❌ Error cargando cuentas activas:', error);
      try {
        const allVentasSnapshot = await getDocs(collection(db, 'ventasOnline'));
        const cuentas = [];
        for (const doc of allVentasSnapshot.docs) {
          const data = doc.data();
          if (data.uid === userUid && data.activo !== false) {
            const diasRestantes = calcularDiasRestantes(data.fechaCorte);
            const estado = obtenerEstadoCuenta(diasRestantes);
            const credenciales = await obtenerCredencialesCuenta(data.codigo);
            const nombrePerfil = data.nombrePerfil || 'No especificado';
            const pinPerfil = data.pinPerfil || null;
            cuentas.push({
              id: doc.id,
              codigo: data.codigo,
              plataforma: data.plataforma,
              fechaInicio: formatearFecha(data.fechaInicio),
              fechaCorte: formatearFecha(data.fechaCorte),
              diasRestantes: diasRestantes,
              estado: estado,
              correo: data.correo,
              numero: data.numero,
              medioPago: data.medioPago,
              idTransaccion: data.idTransaccion,
              tipoCuenta: data.tipoCuenta,
              diasDuracion: data.diasDuracion,
              nombreComprador: data.nombreComprador,
              createdAt: data.createdAt,
              nombrePerfil: nombrePerfil,
              pinPerfil: pinPerfil,
              numeroPerfil: data.numeroPerfil || null,
              emailCuenta: credenciales.emailCuenta,
              passwordCuenta: credenciales.passwordCuenta
            });
          }
        }
        cuentas.sort((a, b) => a.diasRestantes - b.diasRestantes);
        setCuentasActivas(cuentas);
        setCuentasFiltradas(cuentas);
      } catch (fallbackError) {
        console.error('❌ Error en método de respaldo:', fallbackError);
        setCuentasActivas([]);
        setCuentasFiltradas([]);
      }
    } finally {
      setLoadingCuentas(false);
    }
  };

  // NUEVA FUNCIÓN: Aplicar filtros
  const aplicarFiltros = () => {
    let cuentasFiltradas = [...cuentasActivas];

    if (serviceFilter) {
      cuentasFiltradas = cuentasFiltradas.filter(cuenta =>
        cuenta.plataforma.toLowerCase().includes(serviceFilter.toLowerCase())
      );
    }

    if (codeFilter) {
      cuentasFiltradas = cuentasFiltradas.filter(cuenta =>
        cuenta.codigo.toLowerCase().includes(codeFilter.toLowerCase())
      );
    }

    setCuentasFiltradas(cuentasFiltradas);
  };

  // Efecto para autenticación y carga de datos
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/');
        return;
      }
      setUid(user.uid);
      await cargarPlataformas();
      await cargarCuentasActivas(user.uid);
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  // Efecto para aplicar filtros cuando cambian
  useEffect(() => {
    aplicarFiltros();
  }, [serviceFilter, codeFilter, cuentasActivas]);

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinner} />
        <p>Cargando cuentas activas…</p>
      </div>
    );
  }

  return (
    <>
      <NavBar />

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Cuentas Activas</h1>
          <div className={styles.actions}>
            <button
              className={styles.secondaryBtn}
              onClick={() => navigate('/account')}
            >
              Volver a Mi Cuenta
            </button>
            <button
              className={styles.primaryBtn}
              onClick={() => cargarCuentasActivas(uid)}
              disabled={loadingCuentas}
            >
              {loadingCuentas ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        {/* NUEVA SECCIÓN: Filtros */}
        <div className={styles.filterSection}>
          <select
            value={serviceFilter}
            onChange={e => setServiceFilter(e.target.value)}
            className={styles.filterInput}
          >
            <option value="">Filtrar por plataforma</option>
            {plataformas.map(plataforma => (
              <option key={plataforma} value={plataforma}>{plataforma}</option>
            ))}
          </select>
          <span className={styles.filterOr}>o</span>
          <input 
            type="text" 
            placeholder="Filtrar por código" 
            value={codeFilter} 
            onChange={e => setCodeFilter(e.target.value)} 
            className={styles.filterInput} 
          />
        </div>

        {loadingCuentas ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className={styles.spinner} style={{ margin: '0 auto 20px' }} />
            <p>Cargando cuentas activas...</p>
          </div>
        ) : cuentasFiltradas.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>{cuentasActivas.length === 0 ? 'No tienes cuentas activas' : 'No se encontraron cuentas con los filtros aplicados'}</h3>
            <p>
              {cuentasActivas.length === 0 
                ? 'Cuando realices una compra, aquí aparecerán tus cuentas activas con sus fechas de vencimiento.'
                : 'Intenta ajustar los filtros para ver más resultados.'
              }
            </p>
            {cuentasActivas.length === 0 && (
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/page-store')}
              >
                Ir a la tienda
              </button>
            )}
          </div>
        ) : (
          <div className={styles.cuentasGrid}>
            {cuentasFiltradas.map((cuenta) => (
              <div key={cuenta.id} className={styles.cuentaCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.plataformaInfo}>
                    <h3>{cuenta.plataforma}</h3>
                    <span className={styles.tipoCuenta}>{cuenta.tipoCuenta}</span>
                  </div>
                  <div 
                    className={styles.estadoBadge} 
                    style={{ backgroundColor: cuenta.estado.color }}
                  >
                    {cuenta.estado.texto}
                  </div>
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.infoRow}>
                    <span className={styles.label}>Código:</span>
                    <code className={styles.codigo}>{cuenta.codigo}</code>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Email cuenta:</span>
                    <code className={styles.emailCuenta}>{cuenta.emailCuenta}</code>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Contraseña:</span>
                    <code className={styles.passwordCuenta}>{cuenta.passwordCuenta}</code>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Perfil:</span>
                    <span className={styles.nombrePerfil}>
                      {cuenta.nombrePerfil}
                      {cuenta.numeroPerfil && ` (#${cuenta.numeroPerfil})`}
                    </span>
                  </div>

                  {cuenta.pinPerfil && cuenta.pinPerfil !== 'N/A' && cuenta.pinPerfil !== null && (
                    <div className={styles.infoRow}>
                      <span className={styles.label}>PIN:</span>
                      <code className={styles.pinPerfil}>{cuenta.pinPerfil}</code>
                    </div>
                  )}

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Fecha inicio:</span>
                    <span>{cuenta.fechaInicio}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Fecha corte:</span>
                    <span>{cuenta.fechaCorte}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Días restantes:</span>
                    <span 
                      className={styles.diasRestantes}
                      style={{ 
                        color: cuenta.diasRestantes <= 3 ? '#ef4444' : 
                               cuenta.diasRestantes <= 7 ? '#f59e0b' : '#22c55e',
                        fontWeight: 'bold'
                      }}
                    >
                      {cuenta.diasRestantes} días
                    </span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Duración:</span>
                    <span>{cuenta.diasDuracion} días</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Comprador:</span>
                    <span>{cuenta.nombreComprador}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>Medio de pago:</span>
                    <span className={styles.medioPago}>{cuenta.medioPago}</span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.label}>ID transacción:</span>
                    <code className={styles.transactionId}>
                      {cuenta.idTransaccion || 'N/A'}
                    </code>
                  </div>

                  <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => abrirModalRenovacion(cuenta)}
                    >
                      Renovar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <footer className={styles.footer}>
          © 2025 BearMax - Todos los derechos reservados
        </footer>
      </div>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className={styles.modalOverlay}
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className={styles.modal}>
            <div className={styles.modalBody}>
              <div className={styles.modalHeader}>
                <div>
                  <h3 className={styles.modalTitle}>Renovar servicio</h3>
                  {modalData.cuenta && (
                    <p className={styles.modalSub}>
                      {modalData.cuenta.plataforma} — {modalData.cuenta.tipoCuenta}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 10, minHeight: 68 }}>
                {modalData.loading && (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div className={styles.spinner} style={{ margin: '0 auto' }} />
                    <p style={{ marginTop: 10, color: '#94a3b8' }}>Buscando precio...</p>
                  </div>
                )}

                {modalData.error && (
                  <div style={{ padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px' }}>
                    <p style={{ color: '#ef4444', margin: 0, fontSize: '14px' }}>{modalData.error}</p>
                  </div>
                )}

                {!modalData.loading && !modalData.error && (
                  <>
                    <div className={styles.modalRow}>
                      <span className={styles.modalLabel}>Valor a pagar</span>
                      <span className={styles.modalPrice}>{formatCOP(modalData.precio)}</span>
                    </div>

                    {modalData.duracion && (
                      <div className={styles.modalRow}>
                        <span className={styles.modalLabel}>Duración</span>
                        <span className={styles.modalPrice}>{modalData.duracion}</span>
                      </div>
                    )}

                    <p className={styles.modalHint}>
                      El valor se obtuvo del{" "}
                      {modalData.origen === "catalogoVendedores"
                        ? "Catálogo de Vendedores"
                        : "Catálogo"}
                      .
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={`${styles.secondaryBtn} ${styles.modalSecondary}`}
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                className={`${styles.primaryBtn} ${styles.modalPrimary}`}
                onClick={continuarPagoRenovacion}
                disabled={modalData.loading || !!modalData.error || modalData.precio == null}
              >
                Continuar con el pago
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}